import type { VideoCategory } from "@/types/content";

export const videoCategories: Array<"全部" | VideoCategory> = [
  "全部",
  "经典挠痒",
  "搞笑挠痒",
  "互动挠痒",
];

export const durationOptions = [
  { value: "all", label: "全部时长" },
  { value: "short", label: "3 分钟以内" },
  { value: "medium", label: "3 - 6 分钟" },
  { value: "long", label: "6 分钟以上" },
] as const;

export type DurationKey = (typeof durationOptions)[number]["value"];

export const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "最热优先" },
] as const;

export type SortKey = (typeof sortOptions)[number]["value"];

interface VideoFilterPanelProps {
  category: string;
  onCategoryChange: (value: string) => void;
  sort: SortKey;
  onSortChange: (value: SortKey) => void;
  duration: DurationKey;
  onDurationChange: (value: DurationKey) => void;
  tags: string[];
  activeTags: string[];
  onTagToggle: (tag: string) => void;
  onReset: () => void;
  open: boolean;
  onToggle: () => void;
  resultCount: number;
}

export default function VideoFilterPanel({
  category,
  onCategoryChange,
  sort,
  onSortChange,
  duration,
  onDurationChange,
  tags,
  activeTags,
  onTagToggle,
  onReset,
  open,
  onToggle,
  resultCount,
}: VideoFilterPanelProps) {
  const hasFilter = category !== "全部" || duration !== "all" || activeTags.length > 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center gap-3 border-b border-background-200 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {videoCategories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onCategoryChange(item)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                category === item
                  ? "bg-foreground-900 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden items-center gap-1 text-xs text-foreground-500 sm:flex">
            <i className="ri-list-check-2"></i>
            {resultCount} 个结果
          </span>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} p-4 md:block`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-500">排序</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onSortChange(item.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    sort === item.value
                      ? "bg-primary-100 font-medium text-primary-800"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-500">时长</span>
            <div className="flex flex-wrap gap-2">
              {durationOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onDurationChange(item.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    duration === item.value
                      ? "bg-primary-100 font-medium text-primary-800"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <span className="w-16 shrink-0 pt-1 text-xs text-foreground-500">标签</span>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => onTagToggle(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
                    activeTags.includes(tag)
                      ? "border-secondary-500 bg-secondary-500 text-background-50"
                      : "border-background-300 text-foreground-600 hover:border-secondary-300 hover:bg-secondary-50"
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-background-100 pt-3">
            <span className="text-xs text-foreground-500">
              {hasFilter ? "已应用筛选条件" : "当前未筛选全部内容"}
            </span>
            <button
              type="button"
              onClick={onReset}
              disabled={!hasFilter}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground-600 transition-colors hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-refresh-line"></i>
              清空筛选
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}